import { PolicyGlobalSearchResultDto } from "utils/api/policyTransactionApi";

// Field keys double as the PolicyGlobalSearchCriteria property names sent to
// the API (BUS_PolicyGlobalSearch params).
export type GlobalSearchFieldKey =
  | "AgentID"
  | "ReportingID"
  | "FileNumber"
  | "PolicyNumber";

export type GlobalSearchValues = Record<GlobalSearchFieldKey, string>;

export type GlobalSearchQueueKey = "qr" | "preclose" | "close" | "history";

// Criteria handed to a queue's tab loader when it is opened from the results.
export type GlobalSearchSeed = {
  criteria: Record<string, string>;
  label: string;
  nonce: number;
};

export const GS_PLACEHOLDER =
  "Search by Agent ID, Reporting ID, File Number or Policy Number";

// Search enables once any one field reaches this many (trimmed) characters.
export const GS_MIN_CHARS = 3;

// `minChars`: shorter (trimmed) values are ignored — File / Policy Number are
// partial matches, so 1–2 characters would match far too broadly.
export const GS_FIELDS: {
  key: GlobalSearchFieldKey;
  label: string;
  placeholder: string;
  minChars?: number;
}[] = [
  { key: "AgentID", label: "Agent ID", placeholder: "e.g. 433333" },
  { key: "ReportingID", label: "Reporting ID", placeholder: "e.g. 0709867" },
  { key: "FileNumber", label: "File Number", placeholder: "e.g. 000880001", minChars: GS_MIN_CHARS },
  { key: "PolicyNumber", label: "Policy Number", placeholder: "e.g. O-9010-000427210", minChars: GS_MIN_CHARS },
];

// `tabIndex` is the queue's position in PolicyManagementHome's TabNavigator.
// History is not counted (EXISTS only), so it reports "records found".
export const GS_QUEUES: {
  key: GlobalSearchQueueKey;
  label: string;
  tabIndex: number;
  countable: boolean;
}[] = [
  { key: "qr", label: "Quality Review", tabIndex: 0, countable: true },
  { key: "preclose", label: "Pre-Close Queue", tabIndex: 1, countable: true },
  { key: "close", label: "Close Queue", tabIndex: 2, countable: true },
  { key: "history", label: "History", tabIndex: 3, countable: false },
];

export const EMPTY_GS_VALUES: GlobalSearchValues = {
  AgentID: "",
  ReportingID: "",
  FileNumber: "",
  PolicyNumber: "",
};

export type GlobalSearchCriterion = {
  key: GlobalSearchFieldKey;
  label: string;
  value: string;
};

// Filled fields only (meeting each field's minChars), trimmed, in display order.
export const readCriteria = (values: GlobalSearchValues): GlobalSearchCriterion[] =>
  GS_FIELDS.filter((f) => values[f.key].trim().length >= (f.minChars ?? 1))
    .map((f) => ({ key: f.key, label: f.label, value: values[f.key].trim() }));

// Trigger text, e.g. "Agent ID 433333  +  Policy Number O-9010". Null = placeholder.
export const buildSummary = (values: GlobalSearchValues): string | null => {
  const crit = readCriteria(values);
  return crit.length ? crit.map((c) => `${c.label} ${c.value}`).join("  +  ") : null;
};

export const isSearchEnabled = (values: GlobalSearchValues): boolean =>
  GS_FIELDS.some((f) => values[f.key].trim().length >= GS_MIN_CHARS) &&
  readCriteria(values).length > 0;

// Tab-loader criteria keyed by PolicyBaseCriteria property names. Agent ID and
// Reporting ID are exact matches (same as the search); File Number and Policy
// Number use the partial-match FileNumberFilter / PolicyNumberFilter so the tab
// shows exactly the rows the search counted.
export const toTabCriteria = (values: GlobalSearchValues): Record<string, string> => {
  const map: Record<GlobalSearchFieldKey, string> = {
    AgentID: "AgentIDList",
    ReportingID: "ReportingID",
    FileNumber: "FileNumberFilter",
    PolicyNumber: "PolicyNumberFilter",
  };
  return Object.fromEntries(readCriteria(values).map((c) => [map[c.key], c.value]));
};

// Seed keys that share a name with a queue grid header filter, so the value can
// be shown in that column's search box. File / Policy Number seed as *Filter
// (partial match) while the grid uses *List, so they are not included.
const GS_HEADER_FILTER_KEYS = ["AgentIDList", "ReportingID"];

export const toHeaderFilterDefaults = (
  criteria?: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    GS_HEADER_FILTER_KEYS.filter((k) => criteria?.[k]).map((k) => [k, criteria![k]])
  );

export type GlobalSearchQueueRow = {
  key: GlobalSearchQueueKey;
  label: string;
  tabIndex: number;
  countable: boolean;
  count: number;
  enabled: boolean;
};

export const toQueueRows = (result: PolicyGlobalSearchResultDto): GlobalSearchQueueRow[] => {
  const counts: Record<GlobalSearchQueueKey, number> = {
    qr: result.QualityReviewCount ?? 0,
    preclose: result.PreCloseCount ?? 0,
    close: result.CloseCount ?? 0,
    history: result.HasHistoryRecords ? 1 : 0,
  };
  return GS_QUEUES.map((q) => ({ ...q, count: counts[q.key], enabled: counts[q.key] > 0 }));
};
